import { CarWriter } from '@ipld/car';
import drain from 'it-drain';
import map from 'it-map';
import { createUnsafe } from 'multiformats/block';
import defer from 'p-defer';
import PQueue from 'p-queue';
import { DAG_WALK_QUEUE_CONCURRENCY } from './constants.js';
import { SubgraphExporter } from './export-strategies/subgraph-exporter.js';
import { GraphSearch } from './traversal-strategies/graph-search.js';
export class Car {
    components;
    log;
    constructor(components, init) {
        this.components = components;
        this.log = components.logger.forComponent('helia:car');
    }
    async import(reader, options) {
        await drain(this.components.blockstore.putMany(map(reader.blocks(), ({ cid, bytes }) => ({ cid, block: bytes })), options));
    }
    async export(root, writer, options) {
        const deferred = defer();
        const roots = Array.isArray(root) ? root : [root];
        // Create traversal-specific context
        const traversalContext = {
            currentPath: [],
            pathsToTarget: null
        };
        const traversalStrategy = options?.traversal;
        const exportStrategy = options?.exporter ?? new SubgraphExporter();
        // use a queue to walk the DAG instead of recursion so we can traverse very
        // large DAGs
        const queue = new PQueue({
            concurrency: DAG_WALK_QUEUE_CONCURRENCY
        });
        let startedExport = false;
        queue.on('idle', () => {
            if (startedExport) {
                // idle event was called, and started exporting, so we are done.
                deferred.resolve();
            }
            else if (!startedExport && traversalContext.pathsToTarget?.length === roots.length) {
                // queue is idle, we haven't started exporting yet, and we have path(s)
                // to the target(s), so we can start the export process.
                this.log.trace('starting export of blocks to the car file');
                startedExport = true;
                for (const path of traversalContext.pathsToTarget) {
                    const targetIndex = path.length - 1;
                    const targetCid = path[targetIndex];
                    // Process all verification blocks in the path except the target
                    path.slice(0, -1).forEach(cid => {
                        void queue.add(async () => {
                            await this.#exportDagNode({ cid, queue, writer, strategy: exportStrategy, options, recursive: false });
                        })
                            .catch((err) => {
                            this.log.error('error during queue operation - %e', err);
                        });
                    });
                    // Process the target block (which will recursively export its DAG)
                    void queue.add(async () => {
                        await this.#exportDagNode({ cid: targetCid, queue, writer, strategy: exportStrategy, options });
                    })
                        .catch((err) => {
                        this.log.error('error during queue operation - %e', err);
                    });
                }
            }
            else {
                // queue is idle, we haven't started exporting yet, and we don't have
                // path(s) to the target(s), so we can't start the export process.
                // this should not happen without a separate error during traversal, but
                // we'll handle it here anyway.
                this.log.trace('no paths to target, skipping export');
                deferred.reject(new Error('Could not traverse to target CID(s)'));
            }
        });
        queue.on('error', (err) => {
            queue.clear();
            deferred.reject(err);
        });
        for (const root of roots) {
            void queue.add(async () => {
                this.log.trace('traversing dag from %c', root);
                await this.#traverseDagNode({ cid: root, queue, strategy: traversalStrategy ?? new GraphSearch(root), traversalContext, parentPath: [], options });
            })
                .catch((err) => {
                this.log.error('error during queue operation - %e', err);
            });
        }
        // wait for the writer to end
        try {
            await deferred.promise;
        }
        finally {
            await writer.close();
        }
    }
    async *stream(root, options) {
        const { writer, out } = CarWriter.create(root);
        // has to be done async so we write to `writer` and read from `out` at the
        // same time
        this.export(root, writer, options)
            .catch((err) => {
            this.log.error('error during streaming export - %e', err);
        });
        for await (const buf of out) {
            yield buf;
        }
    }
    /**
     * Traverse a DAG and stop when we reach the target node
     */
    async #traverseDagNode({ cid, queue, strategy, traversalContext, parentPath, options }) {
        // if we are traversing, we need to gather path(s) to the target(s)
        const currentPath = [...parentPath, cid];
        if (strategy.isTarget(cid)) {
            traversalContext.pathsToTarget = traversalContext.pathsToTarget ?? [];
            traversalContext.pathsToTarget.push([...currentPath]);
            this.log.trace('found path to target %c', cid);
            return;
        }
        const codec = await this.components.getCodec(cid.code);
        const bytes = await this.components.blockstore.get(cid, options);
        // we are recursively traversing the dag
        const decodedBlock = createUnsafe({ bytes, cid, codec });
        for await (const nextCid of strategy.traverse(cid, decodedBlock)) {
            void queue.add(async () => {
                await this.#traverseDagNode({ cid: nextCid, queue, strategy, traversalContext, parentPath: currentPath ?? [], options });
            })
                .catch((err) => {
                this.log.error('error during traversal queue operation - %e', err);
            });
        }
    }
    /**
     * Use an ExportStrategy to export part of all of a DAG
     */
    async #exportDagNode({ cid, queue, writer, strategy, options, recursive = true }) {
        if (options?.blockFilter?.has(cid.multihash.bytes) === true) {
            return;
        }
        const codec = await this.components.getCodec(cid.code);
        const bytes = await this.components.blockstore.get(cid, options);
        // Mark as processed
        options?.blockFilter?.add(cid.multihash.bytes);
        // Write to CAR
        await writer.put({ cid, bytes });
        if (recursive) {
            // we are recursively traversing the dag
            const decodedBlock = createUnsafe({ bytes, cid, codec });
            for await (const nextCid of strategy.export(cid, decodedBlock)) {
                void queue.add(async () => {
                    await this.#exportDagNode({ cid: nextCid, queue, writer, strategy, options });
                })
                    .catch((err) => {
                    this.log.error('error during export queue operation - %e', err);
                });
            }
        }
    }
}
//# sourceMappingURL=car.js.map